import {
    FETCH_NEWS_FEED_COMMENTS_START,
    FETCH_NEWS_FEED_COMMENTS_SUCCESS,
    FETCH_NEWS_FEED_COMMENTS_FAIL,
    ADD_NEWS_FEED_COMMENT_START,
    ADD_NEWS_FEED_COMMENT_SUCCESS,
    ADD_NEWS_FEED_COMMENT_FAIL,
    EDIT_NEWS_FEED_COMMENT_START,
    EDIT_NEWS_FEED_COMMENT_SUCCESS,
    EDIT_NEWS_FEED_COMMENT_FAIL,
    ADD_NEWS_FEED_HIGHLIGHTED_COMMENTS,
    REMOVE_NEWS_FEED_COMMENT_START,
    REMOVE_NEWS_FEED_COMMENT_SUCCESS,
    REMOVE_NEWS_FEED_COMMENT_FAIL,
    UPDATE_NEWS_FEED_COMMENT_LIKED_BY_USER,
    RESET_NEWS_FEED_COMMENTS,
    FETCH_NEWS_FEED_COMMENT_LIKERS_SUCCESS,
    FETCH_NEWS_FEED_COMMENT_LIKERS_FAIL,
    UPDATE_NEWS_FEED_COMMENT_LIKERS
} from 'actions/ActionTypes';

const INITIAL_STATE = {
    comments: {},
    commentsFetching: {},
    commentsFetchSuccess: {},
    commentsFetchFail: {},
    commentAdding: {},
    commentAddSuccess: {},
    commentAddFail: {},
    commentEditing: { conversationCommentUuid: null, editing: false },
    commentEditSuccess: { conversationCommentUuid: null, success: false },
    commentEditFail: { conversationCommentUuid: null, fail: false },
    highlightedComments: {},
    commentRemoving: false,
    commentRemoveSuccess: false,
    commentRemoveFail: false,
    commentLikers: {},
    initialCommentsFetchDone: {},
    errorRenderKey: 0
};

// we need to reset commentsFetchSuccess & commentsFetchFail when new fetch starts,
// so the subscribed components could sense the correct prop change, i.e., from false to true
const commentsFetching = (state, { activityId }) => ({
    ...state,
    commentsFetching: { ...state.commentsFetching, [activityId]: true },
    commentsFetchSuccess: { ...state.commentsFetchSuccess, [activityId]: false },
    commentsFetchFail: { ...state.commentsFetchFail, [activityId]: false }
});

const commentsFetchFinished = (isSuccess) => (state, { data, activityId }) => {
    let newComments;
    if (isSuccess) {
        newComments = addComments(activityId, state.comments, data, true);
    }
    return {
        ...state,
        comments: isSuccess ? newComments : state.comments,
        commentsFetching: { ...state.commentsFetching, [activityId]: false },
        commentsFetchSuccess: { ...state.commentsFetchSuccess, [activityId]: isSuccess },
        commentsFetchFail: { ...state.commentsFetchFail, [activityId]: !isSuccess },
        initialCommentsFetchDone: isSuccess
            ? { ...state.initialCommentsFetchDone, [activityId]: true }
            : state.initialCommentsFetchDone
    };
};

const addComments = (activityId, prevComments, commentsToAdd, backwardOrder = false) => {
    let newComments;
    if (!prevComments) {
        newComments = {
            [activityId]: commentsToAdd
        };
    } else {
        newComments = !prevComments[activityId]
            ? {
                  ...prevComments,
                  [activityId]: commentsToAdd
              }
            : {
                  ...prevComments,
                  [activityId]: backwardOrder
                      ? commentsToAdd.concat(prevComments[activityId])
                      : prevComments[activityId].concat(commentsToAdd)
              };
    }
    return newComments;
};

const commentAdding = (state, { activityId }) => ({
    ...state,
    commentAdding: { ...state.commentAdding, [activityId]: true },
    commentAddSuccess: { ...state.commentAddSuccess, [activityId]: false },
    commentAddFail: { ...state.commentAddFail, [activityId]: false }
});

const commentAddFinished = (isSuccess) => (state, { data, activityId, errorReachLimit }) => {
    let newComments;
    let newHighlightedComments;
    let newErrorRenderKey = state.errorRenderKey;
    if (isSuccess) {
        // add one comment to both comments and highlightedComments array
        newComments = addComments(activityId, state.comments, [data]);
        // copy object to prevent updating to the same object later,
        // e.g., updateNewsFeedCommentLikedByUser will update both comments and highlightedComments
        newHighlightedComments = addComments(activityId, state.highlightedComments, [{ ...data }]);
    } else {
        newErrorRenderKey = Date.now();
    }

    return {
        ...state,
        commentAdding: { ...state.commentAdding, [activityId]: false },
        commentAddSuccess: { ...state.commentAddSuccess, [activityId]: isSuccess },
        commentAddFail: { ...state.commentAddFail, [activityId]: !isSuccess && !errorReachLimit },
        comments: isSuccess ? newComments : state.comments,
        highlightedComments: isSuccess ? newHighlightedComments : state.highlightedComments,
        errorRenderKey: newErrorRenderKey
    };
};

const commentEditing = (state, { conversationCommentUuid }) => ({
    ...state,
    commentEditing: { conversationCommentUuid, editing: true },
    commentEditSuccess: { conversationCommentUuid, success: false },
    commentEditFail: { conversationCommentUuid, fail: false }
});

const commentEditFinished = (isSuccess) => (state, action) => {
    const { data, activityId, conversationCommentUuid } = action;
    let updateComments = false;
    let updateHighlightedComments = false;
    let newErrorRenderKey = state.errorRenderKey;
    if (isSuccess) {
        updateComments =
            state.comments &&
            Array.isArray(state.comments[activityId]) &&
            state.comments[activityId].some((comment) => comment.conversationCommentUuid === conversationCommentUuid);
        updateHighlightedComments =
            state.highlightedComments &&
            Array.isArray(state.highlightedComments[activityId]) &&
            state.highlightedComments[activityId].some(
                (comment) => comment.conversationCommentUuid === conversationCommentUuid
            );
    } else {
        newErrorRenderKey = Date.now();
    }
    return {
        ...state,
        commentEditing: { conversationCommentUuid, editing: false },
        commentEditSuccess: { conversationCommentUuid, success: isSuccess },
        commentEditFail: { conversationCommentUuid, fail: !isSuccess },
        comments:
            isSuccess && updateComments
                ? {
                      ...state.comments,
                      [activityId]: state.comments[activityId].map((comment) => {
                          if (comment.conversationCommentUuid === conversationCommentUuid) {
                              const { body, updated, commentBlockList } = data;
                              return {
                                  ...comment,
                                  body,
                                  commentBlockList,
                                  updated
                              };
                          }
                          return comment;
                      })
                  }
                : state.comments,
        highlightedComments:
            isSuccess && updateHighlightedComments
                ? {
                      ...state.highlightedComments,
                      [activityId]: state.highlightedComments[activityId].map((comment) => {
                          if (comment.conversationCommentUuid === conversationCommentUuid) {
                              const { body, updated, commentBlockList } = data;
                              return {
                                  ...comment,
                                  body,
                                  commentBlockList,
                                  updated
                              };
                          }
                          return comment;
                      })
                  }
                : state.highlightedComments,
        errorRenderKey: newErrorRenderKey
    };
};

const addNewsFeedHighlightedComments = (state, action) => {
    const { activityList } = action.data;
    if (!activityList) return state; // no valid data, do nothing here

    const highlightedComments = { ...state.highlightedComments };
    activityList.forEach((activity) => {
        const { highlightedComment, activityId } = activity;
        if (highlightedComment) {
            highlightedComments[activityId] = [highlightedComment];
        }
    });

    return {
        ...state,
        highlightedComments
    };
};

const commentRemoving = (state) => ({
    ...state,
    commentRemoving: true
});

const commentRemoveFinished = (isSuccess) => (state, action) => {
    const { activityId, conversationCommentUuid } = action;
    let updateComments = false;
    let updateHighlightedComments = false;
    if (isSuccess) {
        updateComments =
            state.comments &&
            Array.isArray(state.comments[activityId]) &&
            state.comments[activityId].some((comment) => comment.conversationCommentUuid === conversationCommentUuid);
        updateHighlightedComments =
            state.highlightedComments &&
            Array.isArray(state.highlightedComments[activityId]) &&
            state.highlightedComments[activityId].some(
                (comment) => comment.conversationCommentUuid === conversationCommentUuid
            );
    }

    return {
        ...state,
        commentRemoving: false,
        commentRemoveSuccess: isSuccess,
        commentRemoveFail: !isSuccess,
        comments:
            isSuccess && updateComments
                ? {
                      ...state.comments,
                      [activityId]: state.comments[activityId].filter(
                          (comment) => comment.conversationCommentUuid !== conversationCommentUuid
                      )
                  }
                : state.comments,
        highlightedComments:
            isSuccess && updateHighlightedComments
                ? {
                      ...state.highlightedComments,
                      [activityId]: state.highlightedComments[activityId].filter(
                          (comment) => comment.conversationCommentUuid !== conversationCommentUuid
                      )
                  }
                : state.highlightedComments
    };
};

const updateNewsFeedCommentLikedByUser = (state, action) => {
    const { activityId, conversationCommentUuid, likedByUser, fullName, displayName } = action.payload;

    const updateComments =
        state.comments &&
        Array.isArray(state.comments[activityId]) &&
        state.comments[activityId].some((comment) => comment.conversationCommentUuid === conversationCommentUuid);
    const updateHighlightedComments =
        state.highlightedComments &&
        Array.isArray(state.highlightedComments[activityId]) &&
        state.highlightedComments[activityId].some(
            (comment) => comment.conversationCommentUuid === conversationCommentUuid
        );

    let newCommentLikers = state.commentLikers[conversationCommentUuid] || [];
    if (likedByUser) {
        newCommentLikers.push({ fullName, displayName });
    } else {
        newCommentLikers = newCommentLikers.filter((liker) => liker.fullName !== fullName);
    }

    return {
        ...state,
        comments: updateComments
            ? {
                  ...state.comments,
                  [activityId]: state.comments[activityId].map((comment) => {
                      if (comment.conversationCommentUuid === conversationCommentUuid) {
                          comment.likedByUser = likedByUser;
                          comment.numberOfLikes = likedByUser ? comment.numberOfLikes + 1 : comment.numberOfLikes - 1;
                      }
                      return comment;
                  })
              }
            : state.comments,
        highlightedComments: updateHighlightedComments
            ? {
                  ...state.highlightedComments,
                  [activityId]: state.highlightedComments[activityId].map((comment) => {
                      if (comment.conversationCommentUuid === conversationCommentUuid) {
                          comment.likedByUser = likedByUser;
                          comment.numberOfLikes = likedByUser ? comment.numberOfLikes + 1 : comment.numberOfLikes - 1;
                          // for comment added by user, it won't contain 'likerFullNames', and we don't need to handle that
                          if (comment.hasOwnProperty('likerFullNames')) {
                              comment.likerFullNames = likedByUser
                                  ? comment.likerFullNames.concat([fullName])
                                  : comment.likerFullNames.filter((name) => name !== fullName);
                          }
                      }
                      return comment;
                  })
              }
            : state.highlightedComments,
        commentLikers: {
            ...state.commentLikers,
            [conversationCommentUuid]: newCommentLikers
        }
    };
};

/**
 * Not only fetch comment likers, but also update comments and highlightedComments
 * for numberOfLikes and likerFullNames
 */
const fetchNewsFeedCommentLikersFinished = (isSuccess) => (state, action) => {
    if (!isSuccess) return state;

    return updateNewsFeedCommentLikers(state, action);
};

const updateNewsFeedCommentLikers = (state, { activityId, commentUuid, likers, numberOfLikes }) => {
    const updateComments =
        state.comments &&
        Array.isArray(state.comments[activityId]) &&
        state.comments[activityId].some((comment) => comment.conversationCommentUuid === commentUuid);
    const updateHighlightedComments =
        state.highlightedComments &&
        Array.isArray(state.highlightedComments[activityId]) &&
        state.highlightedComments[activityId].some((comment) => comment.conversationCommentUuid === commentUuid);

    return {
        ...state,
        comments: updateComments
            ? {
                  ...state.comments,
                  [activityId]: state.comments[activityId].map((comment) => {
                      if (comment.conversationCommentUuid === commentUuid) {
                          comment.numberOfLikes = numberOfLikes;
                      }
                      return comment;
                  })
              }
            : state.comments,
        highlightedComments: updateHighlightedComments
            ? {
                  ...state.highlightedComments,
                  [activityId]: state.highlightedComments[activityId].map((comment) => {
                      if (comment.conversationCommentUuid === commentUuid) {
                          comment.numberOfLikes = numberOfLikes;
                          // for comment added by user, it won't contain 'likerFullNames', and we don't need to handle that
                          if (comment.hasOwnProperty('likerFullNames')) {
                              comment.likerFullNames = likers.map((liker) => liker.fullName || liker.displayName);
                          }
                      }
                      return comment;
                  })
              }
            : state.highlightedComments,
        commentLikers: {
            ...state.commentLikers,
            [commentUuid]: likers
        }
    };
};

const resetNewsFeedComments = () => {
    return INITIAL_STATE;
};

const handlers = {
    [FETCH_NEWS_FEED_COMMENTS_START]: commentsFetching,
    [FETCH_NEWS_FEED_COMMENTS_SUCCESS]: commentsFetchFinished(true),
    [FETCH_NEWS_FEED_COMMENTS_FAIL]: commentsFetchFinished(false),
    [ADD_NEWS_FEED_COMMENT_START]: commentAdding,
    [ADD_NEWS_FEED_COMMENT_SUCCESS]: commentAddFinished(true),
    [ADD_NEWS_FEED_COMMENT_FAIL]: commentAddFinished(false),
    [EDIT_NEWS_FEED_COMMENT_START]: commentEditing,
    [EDIT_NEWS_FEED_COMMENT_SUCCESS]: commentEditFinished(true),
    [EDIT_NEWS_FEED_COMMENT_FAIL]: commentEditFinished(false),
    [ADD_NEWS_FEED_HIGHLIGHTED_COMMENTS]: addNewsFeedHighlightedComments,
    [REMOVE_NEWS_FEED_COMMENT_START]: commentRemoving,
    [REMOVE_NEWS_FEED_COMMENT_SUCCESS]: commentRemoveFinished(true),
    [REMOVE_NEWS_FEED_COMMENT_FAIL]: commentRemoveFinished(false),
    [UPDATE_NEWS_FEED_COMMENT_LIKED_BY_USER]: updateNewsFeedCommentLikedByUser,
    [RESET_NEWS_FEED_COMMENTS]: resetNewsFeedComments,
    [FETCH_NEWS_FEED_COMMENT_LIKERS_SUCCESS]: fetchNewsFeedCommentLikersFinished(true),
    [FETCH_NEWS_FEED_COMMENT_LIKERS_FAIL]: fetchNewsFeedCommentLikersFinished(false),
    [UPDATE_NEWS_FEED_COMMENT_LIKERS]: updateNewsFeedCommentLikers
};

export const getCommentsFetching = (state, activityId) => state.newsFeedComments.commentsFetching[activityId];
export const getCommentsFetchSuccess = (state, activityId) => state.newsFeedComments.commentsFetchSuccess[activityId];
export const getComments = (state, activityId) => state.newsFeedComments.comments[activityId];
export const getCommentAdding = (state) => state.newsFeedComments.commentAdding;
export const getCommentAddSuccess = (state) => state.newsFeedComments.commentAddSuccess;
export const getCommentAddFail = (state) => {
    return Object.values(state.newsFeedComments.commentAddFail).some((value) => value === true);
};
export const getCommentEditingWithId = (state, id) =>
    state.newsFeedComments.commentEditing.conversationCommentUuid === id &&
    state.newsFeedComments.commentEditing.editing;
export const getCommentEditFailWithId = (state, id) =>
    state.newsFeedComments.commentEditFail.conversationCommentUuid === id &&
    state.newsFeedComments.commentEditFail.fail;
export const getCommentEditFail = (state) => state.newsFeedComments.commentEditFail.fail;
export const getCommentErrorRenderKey = (state) => state.newsFeedComments.errorRenderKey;
export const getCommentRemoveFail = (state) => state.newsFeedComments.commentRemoveFail;
export const getHighlightedComments = (state, activityId) => state.newsFeedComments.highlightedComments[activityId];
export const getCommentLikers = (state, id) => state.newsFeedComments.commentLikers[id];

export default function newsFeedComments(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
